import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputLabel, Stack, TextField, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React from 'react';
import { Translate } from 'react-jhipster';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface IValidatedFormsProps {
  sendSensorDataByImplement?: any;
  setDateStart?: any;
  dateStart?: any;
  dateEnd?: any;
  setDateEnd?: any;
  blockDates?: boolean;
  isOpen?: any;
  toggle?: any;
  noTime?: boolean;
  monthOnly?: boolean;
  submitLabel?: string;
  maxRangeDays?: number;
  maxRangeDisplayDays?: number;
}

const getValidDayjs = value => {
  if (!value) return null;

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export const ValidatedFormsNew = ({
  sendSensorDataByImplement,
  setDateStart,
  dateStart,
  setDateEnd,
  dateEnd,
  blockDates = false,
  isOpen,
  toggle,
  noTime,
  monthOnly,
  submitLabel,
  maxRangeDays,
  maxRangeDisplayDays,
}: IValidatedFormsProps) => {
  const beforeYesterday = dayjs().subtract(2, 'day').endOf('day');
  const start = getValidDayjs(dateStart);
  const end = getValidDayjs(dateEnd);
  const minEndDate = end ? (maxRangeDays !== undefined ? end.subtract(maxRangeDays, 'day').startOf('day') : end.subtract(36, 'month').endOf('day')) : null;
  const maxEndDate = start ? (maxRangeDays !== undefined ? start.add(maxRangeDays, 'day').endOf('day') : start.add(36, 'month').endOf('day')) : null;
  const invalidRange =
    maxRangeDays !== undefined && start && end && (end.isBefore(start) || end.startOf('day').diff(start.startOf('day'), 'day') > maxRangeDays);
  const hasInvalidDate = !start || !end;
  const views = monthOnly
    ? (['year', 'month'] as const)
    : noTime
    ? (['year', 'month', 'day'] as const)
    : (['year', 'month', 'day', 'hours', 'minutes'] as const);
  const inputFormat = monthOnly ? 'MM/YYYY' : noTime ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm';

  // While the user types in the masked input, MUI parses the partial text on every keystroke
  // and forwards the result to onChange even when it is an Invalid Date. Propagating those
  // upward breaks consumers that call toISOString()/format() ("RangeError: Invalid time value"),
  // so incomplete input is ignored until it parses to a valid date.
  const parseIfValid = (value: any): dayjs.Dayjs | null => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  };

  // The masked input only reports a value once the whole inputFormat is filled in, so a date typed
  // without the time part never reaches onChange and would be lost. Reparsing the raw text on blur
  // lets the user type only the date; the missing time is then filled with the period boundary.
  const parseTypedText = (text: string): { date: dayjs.Dayjs; complete: boolean } | null => {
    const trimmed = (text ?? '').trim();
    if (!trimmed) return null;

    const complete = dayjs(trimmed, inputFormat, true);
    if (complete.isValid()) {
      return { date: complete, complete: true };
    }

    const datePattern = monthOnly ? /^\d{2}\/\d{4}/ : /^\d{2}\/\d{2}\/\d{4}/;
    const datePart = datePattern.exec(trimmed);
    if (!datePart) return null;

    const parsed = dayjs(datePart[0], monthOnly ? 'MM/YYYY' : 'DD/MM/YYYY', true);
    return parsed.isValid() ? { date: parsed, complete: false } : null;
  };

  const commitStart = (parsed: dayjs.Dayjs, value: any) => {
    if (monthOnly) {
      setDateStart(parsed.startOf('month').toDate());
    } else if (noTime) {
      setDateStart(parsed.startOf('day').toDate());
    } else {
      setDateStart(value);
    }

    if (dateEnd && parsed.isAfter(dayjs(dateEnd))) {
      setDateEnd(null);
    }
  };

  const commitEnd = (parsed: dayjs.Dayjs, value: any) => {
    if (dateStart && parsed.isBefore(dayjs(dateStart))) {
      setDateEnd(null);
      return;
    }

    if (monthOnly) {
      setDateEnd(parsed.endOf('month').toDate());
    } else if (noTime) {
      setDateEnd(parsed.endOf('day').toDate());
    } else {
      setDateEnd(value);
    }
  };

  const handleStartChange = (newValue: any) => {
    if (!newValue) {
      setDateStart(null);
      return;
    }

    const parsed = parseIfValid(newValue);
    if (parsed) {
      commitStart(parsed, newValue);
    }
  };

  const handleEndChange = (newValue: any) => {
    if (!newValue) {
      setDateEnd(null);
      return;
    }

    const parsed = parseIfValid(newValue);
    if (parsed) {
      commitEnd(parsed, newValue);
    }
  };

  const handleStartBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const typed = parseTypedText(event.target.value);
    if (!typed) {
      setDateStart(null);
      return;
    }

    // A date typed without a time means "from the beginning of that day".
    const value = typed.complete ? typed.date : typed.date.startOf('day');
    commitStart(value, value.toDate());
  };

  const handleEndBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const typed = parseTypedText(event.target.value);
    if (!typed) {
      setDateEnd(null);
      return;
    }

    // A date typed without a time means "until the end of that day".
    const value = typed.complete ? typed.date : typed.date.endOf('day');
    commitEnd(value, value.toDate());
  };

  return (
    <Dialog
      open={isOpen}
      onClose={toggle}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: 'background.default',
          backgroundImage: 'none',
          borderRadius: '8px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1.5rem' }}>
        <Translate contentKey={`fleetSenseApp.periodicType.custom`} />
        <IconButton onClick={toggle} color="secondary">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={sendSensorDataByImplement}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2}>
              <Box>
                <InputLabel>
                  <Typography sx={{ fontWeight: 600, fontSize: '.875rem', color: 'text.primary' }}>
                    <Translate contentKey="fleetSenseApp.dashboard.initial" />
                  </Typography>
                </InputLabel>
                <DateTimePicker
                  views={views}
                  inputFormat={inputFormat}
                  value={dateStart}
                  onChange={newValue => {
                    const validDate = getValidDayjs(newValue);

                    if (!validDate) {
                      setDateStart(null);
                      return;
                    }

                    if (monthOnly) {
                      setDateStart(validDate.startOf('month').toDate());
                    } else if (noTime) {
                      setDateStart(validDate.startOf('day').toDate());
                    } else {
                      setDateStart(newValue);
                    }

                    if (end && validDate.isAfter(end)) {
                      setDateEnd(null);
                    }
                  }}
                  renderInput={params => <TextField {...params} fullWidth error={false} />}
                  ampm={false}
                  minDateTime={blockDates ? minEndDate : end && maxRangeDays !== undefined ? minEndDate : undefined}
                  maxDateTime={blockDates ? beforeYesterday : end && maxRangeDays !== undefined ? end.endOf('day') : undefined}
                  disableFuture
                />
              </Box>
              <Box>
                <InputLabel>
                  <Typography sx={{ fontWeight: 600, fontSize: '.875rem', color: 'text.primary' }}>
                    <Translate contentKey="fleetSenseApp.dashboard.final" />
                  </Typography>
                </InputLabel>
                <DateTimePicker
                  views={views}
                  inputFormat={inputFormat}
                  value={dateEnd}
                  onChange={newValue => {
                    const validDate = getValidDayjs(newValue);

                    if (!validDate) {
                      setDateEnd(null);
                      return;
                    }

                    if (monthOnly) {
                      setDateEnd(validDate.endOf('month').toDate());
                    } else if (noTime) {
                      setDateEnd(validDate.endOf('day').toDate());
                    } else {
                      setDateEnd(newValue);
                    }

                    if (start && validDate.isBefore(start)) {
                      setDateEnd(null);
                    }
                  }}
                  renderInput={params => <TextField {...params} fullWidth error={false} />}
                  ampm={false}
                  minDateTime={blockDates ? minEndDate : start && maxRangeDays !== undefined ? start.startOf('day') : undefined}
                  maxDateTime={blockDates ? beforeYesterday : maxEndDate}
                  disableFuture
                />
              </Box>
            </Stack>
          </LocalizationProvider>
          {invalidRange && (
            <Typography color="error" sx={{ mt: 2 }}>
              <Translate contentKey="fleetSenseApp.reports.validation.invalidRange" interpolate={{ days: maxRangeDisplayDays ?? maxRangeDays }} />
            </Typography>
          )}
          {maxRangeDays !== undefined && (
            <Typography color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
              <Translate contentKey="fleetSenseApp.reports.validation.maxRange" interpolate={{ days: maxRangeDisplayDays ?? maxRangeDays }} />
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 1 }}>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={hasInvalidDate || !!invalidRange}
            sx={{ fontSize: '1rem', textTransform: 'none', padding: '8px 22px', height: '38px' }}
          >
            {submitLabel ? submitLabel : <Translate contentKey="fleetSenseApp.dashboard.search" />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ValidatedFormsNew;
